/** Angular Imports */
import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import {
    catchError,
    finalize,
    Observable,
    of,
    shareReplay,
    switchMap,
    tap,
    throwError,
} from 'rxjs';

/** Local Imports */
import { AuthService } from '@core/services/auth/auth.service';
import { Logger } from '@shared/utils';

/**
 * Auth endpoints that must NOT trigger the expiry handler.
 * - login/validate/session-status: expected to 401 when unauthenticated.
 * - logout/refresh: would loop on themselves.
 */
const AUTH_BYPASS_PREFIXES = [
    '/api/auth/login',
    '/api/auth/logout',
    '/api/auth/validate',
    '/api/auth/session-status',
    '/api/auth/refresh',
];

/**
 * Pages where 401 is expected and should NOT trigger redirect/logout
 * (user is already unauthenticated by design).
 */
const UNAUTHENTICATED_ROUTE_PREFIXES = ['/login', '/auth'];

/**
 * Module-scoped, shared in-flight verification check. Multiple `/api/*` calls
 * commonly 401 together (a page firing several parallel requests) — every one
 * of them awaits and reacts to this SAME check instead of each independently
 * calling `verifyAuthWithBackend()` (which would show duplicate logout
 * notices) or, worse, all-but-the-first being silently ignored.
 */
let expiryCheck$: Observable<boolean> | null = null;

/**
 * HTTP interceptor that detects expired/missing session cookies on `/api/*` calls.
 *
 * @remarks
 * A `401` from a single protected endpoint does **not** by itself prove the
 * session expired — the endpoint may reject for resource-specific reasons while
 * the session is still valid. So on a candidate 401 we re-validate the session
 * with the backend and only log out when validation confirms the session is gone:
 *   1. Re-validate via `verifyAuthWithBackend()`.
 *   2. If still valid → propagate the original error untouched (no logout).
 *   3. If invalid → clear local auth state, wipe the BFF session cookie via
 *      `serverLogout()` (no Auth0 redirect, so the modal renders instead of
 *      being yanked through Auth0), and surface the logout notice modal via
 *      `notifySessionExpired()`. Redirect to `/login` happens on acknowledgement.
 *
 * `403` is treated as authorization-denied (authenticated but forbidden) and
 * never triggers logout.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
    const platformId = inject(PLATFORM_ID);
    const router = inject(Router);
    const authService = inject(AuthService);

    return next(req).pipe(
        catchError((error: unknown) => {
            if (!shouldHandleExpiry(error, req.url, router.url, platformId)) {
                return throwError(() => error);
            }

            // A protected endpoint returned 401 — confirm the session is actually
            // dead before logging the user out, so a resource-specific 401 cannot
            // tear down an otherwise-valid session. Concurrent 401s (a page firing
            // several parallel requests) all await this SAME check rather than each
            // starting their own, so exactly one verify/logout cycle runs regardless
            // of how many requests failed at once.
            if (!expiryCheck$) {
                expiryCheck$ = authService.verifyAuthWithBackend().pipe(
                    tap((stillValid) => {
                        if (stillValid) {
                            Logger.warn(
                                'AuthInterceptor',
                                '401 on protected endpoint but session still valid; not logging out',
                                req.url,
                            );
                            return;
                        }

                        Logger.warn(
                            'AuthInterceptor',
                            'Session expired, showing logout notice',
                            req.url,
                        );
                        authService.clearAuthentication();
                        authService.serverLogout().subscribe();
                        authService.notifySessionExpired();
                    }),
                    catchError(() => of(false)),
                    finalize(() => {
                        expiryCheck$ = null;
                    }),
                    shareReplay(1),
                );
            }

            return expiryCheck$.pipe(switchMap(() => throwError(() => error)));
        }),
    );
};

/**
 * Decides whether the interceptor should treat this error as a candidate
 * session-expired event worth re-validating. Only `401` qualifies — `403`
 * means authenticated-but-forbidden and must not trigger logout.
 */
function shouldHandleExpiry(
    error: unknown,
    requestUrl: string,
    currentRouterUrl: string,
    platformId: object,
): boolean {
    if (!isPlatformBrowser(platformId)) return false;
    if (!(error instanceof HttpErrorResponse)) return false;
    if (error.status !== 401) return false;
    if (!requestUrl.startsWith('/api/')) return false;
    if (AUTH_BYPASS_PREFIXES.some((prefix) => requestUrl.startsWith(prefix))) return false;
    if (UNAUTHENTICATED_ROUTE_PREFIXES.some((p) => currentRouterUrl?.startsWith(p))) return false;
    return true;
}
